import React, { memo, useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  Image,
  TouchableOpacity,
  StyleSheet,
  Dimensions,
  Platform,
  useWindowDimensions,
  type GestureResponderEvent,
} from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import Icon from 'react-native-vector-icons/Feather';
import { Colors, Typography, Spacing, Radius, Shadow } from '../theme';
import type { TonyteActivity } from '../types';
import { getWebFallbackImage, resolveActivityImage, type ResolvedActivityImage } from '../utils/activityImages';
import { TONYTE_PROFILE_IMAGE } from '../utils/userProfiles';
import { useEventUploaderImage } from '../hooks/useEventUploaderImage';
import AmbientGlow from './AmbientGlow';
import { useI18n } from '../i18n';
import { useTranslatedActivity } from '../hooks/useTranslatedActivity';
import { getSafeTags } from '../utils/activityTags';
import { isInWeeklyFeaturedWindow } from '../utils/weeklyFeatured';
import { useTheme } from '../theme/useTheme';
import type { TonyteTheme } from '../theme/themes';

const { width } = Dimensions.get('window');
const CARD_WIDTH = Math.min(width - Spacing.base * 2, 430);
const CARD_HEIGHT = Math.round(CARD_WIDTH * 0.64);

interface Props {
  activity: TonyteActivity;
  onPress: () => void;
  onInterestPress?: () => void;
  onSharePress?: () => void;
  canManage?: boolean;
  onEditPress?: () => void;
  onDeletePress?: () => void;
  // Admin/co-admin only: opens the "Featured This Week" editor for this event.
  canManageFeatured?: boolean;
  onFeaturedPress?: () => void;
  interested?: boolean;
  interestedCount?: number;
}

function formatDate(dateStr: string, locale: string, timeStr?: string): string {
  if (!dateStr) return '';
  const date = new Date(`${dateStr}T12:00:00`);
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', weekday: 'short' };
  const datePart = Number.isNaN(date.getTime()) ? dateStr : date.toLocaleDateString(locale, opts);
  return timeStr ? `${datePart} · ${timeStr}` : datePart;
}

function isOngoingMultiDay(activity: TonyteActivity): boolean {
  if (!activity.endDate || activity.endDate === activity.startDate) return false;
  // Use the real start/end times: overnight events (22:00–02:00) carry the
  // next day as endDate, and a midnight start made them "ongoing" all day
  // before they had actually begun.
  const timeOr = (t: string | undefined, fallback: string) => (/^\d{2}:\d{2}$/.test(t || '') ? `${t}:00` : fallback);
  const now = new Date();
  const start = new Date(`${activity.startDate}T${timeOr(activity.startTime, '00:00:00')}`);
  const end = new Date(`${activity.endDate}T${timeOr(activity.endTime, '23:59:59')}`);
  return start <= now && end >= now;
}

function formatDateLine(activity: TonyteActivity, locale: string, ongoingLabel: string, untilLabel: string): string {
  if (isOngoingMultiDay(activity)) {
    const end = new Date(`${activity.endDate}T12:00:00`);
    const endStr = Number.isNaN(end.getTime())
      ? activity.endDate!
      : end.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
    return `${ongoingLabel} · ${untilLabel} ${endStr}`;
  }
  return formatDate(activity.startDate, locale, activity.startTime);
}

function getStatusText(
  activity: TonyteActivity,
  locale: string,
  runsUntilLabel: string,
  freeEntryLabel: string,
  ticketsFromLabel: string,
): string {
  if (isOngoingMultiDay(activity)) {
    const end = new Date(`${activity.endDate}T12:00:00`);
    const endStr = Number.isNaN(end.getTime())
      ? activity.endDate!
      : end.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
    return `${runsUntilLabel} ${endStr}`;
  }
  if (activity.isFree) return freeEntryLabel;
  return activity.ticketPrice ? `${ticketsFromLabel} ${activity.ticketPrice}` : '';
}

function getLocation(activity: TonyteActivity, tonyteVenueLabel: string): string {
  return activity.venueName || activity.locationName || activity.address || activity.location || activity.city || tonyteVenueLabel;
}

function getTags(activity: TonyteActivity): string[] {
  return getSafeTags([
    ...(activity.tags || []),
    ...(activity.musicGenres || []),
    ...(activity.hashtags || []),
    activity.city,
  ]);
}

const LOCALE_MAP: Record<string, string> = {
  de: 'de-DE', es: 'es-ES', fr: 'fr-FR', ru: 'ru-RU', en: 'en-US',
};

const EventCard: React.FC<Props> = ({
  activity,
  onPress,
  onInterestPress,
  onSharePress,
  canManage,
  onEditPress,
  onDeletePress,
  canManageFeatured,
  onFeaturedPress,
  interested,
  interestedCount,
}) => {
  if (__DEV__) console.count('[Perf][Render] EventCard');
  const featuredNow = isInWeeklyFeaturedWindow(activity);
  const { t, tp, language } = useI18n();
  const { theme } = useTheme();
  const { width: viewportWidth } = useWindowDimensions();
  const isDesktopWeb = Platform.OS === 'web' && viewportWidth >= 1024;
  const themedStyles = useMemo(() => createThemeStyles(theme), [theme]);
  const locale = LOCALE_MAP[language] ?? 'en-US';
  const translatedActivity = useTranslatedActivity(activity);
  const [imageSource, setImageSource] = useState<number | string | null>(null);
  const [imageDebug, setImageDebug] = useState<ResolvedActivityImage | null>(null);
  const [imageFailed, setImageFailed] = useState(false);
  const [usedImageFallback, setUsedImageFallback] = useState(false);
  const [uploaderImageFailed, setUploaderImageFailed] = useState(false);
  const count = interestedCount ?? activity.interestedCount ?? 0;
  const rawTags = useMemo(() => getTags(activity), [activity]);
  const allTags = getSafeTags(translatedActivity.tags.length ? translatedActivity.tags : rawTags);
  const visibleTags = allTags.slice(0, 3);
  const hiddenTagCount = Math.max(0, allTags.length - visibleTags.length);
  // Partner events: the partner's picture (venue logo, then account photo).
  // Admin/curated events: always the Tonyte logo (the fallback below), never
  // the admin's personal photo — so skip both lookups for them.
  const { image: uploaderImage } = useEventUploaderImage(activity);

  useEffect(() => {
    let cancelled = false;
    setImageFailed(false);
    setUsedImageFallback(false);
    setImageSource(null);
    setImageDebug(null);

    resolveActivityImage(activity)
      .then((resolved) => {
        if (cancelled) return;
        setImageSource(resolved.source);
        setImageDebug(resolved);
        if (__DEV__) {
          console.log('[Tonyte][RN][EventImage] visible event image resolved', {
            eventId: activity.id,
            eventTitle: activity.title,
            selectedImageField: resolved.selectedField,
            rawImageValue: resolved.rawValue || null,
            resolvedImageValue: resolved.resolvedValue,
            sourceType: resolved.sourceType,
          });
        }
      })
      .catch((error) => {
        if (cancelled) return;
        setImageFailed(true);
        if (__DEV__) {
          console.warn('[Tonyte][RN][EventImage] failed to resolve image', {
            eventId: activity.id,
            eventTitle: activity.title,
            error: error instanceof Error ? error.message : String(error),
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [activity]);

  useEffect(() => {
    setUploaderImageFailed(false);
  }, [uploaderImage]);

  const showImage = Boolean(imageSource) && !imageFailed;
  const location = getLocation(activity, t('feed.tonyteVenue'));
  const ongoing = isOngoingMultiDay(activity);
  const priceLabel = ongoing ? t('feed.ongoing') : activity.isFree ? t('feed.free') : activity.ticketPrice || t('feed.paidEntry');
  const statusText = getStatusText(activity, locale, t('feed.runsUntil'), t('feed.freeEntry'), t('feed.ticketsFrom'));
  const dateLabel = formatDateLine(activity, locale, t('feed.ongoing'), t('feed.until'));

  if (isDesktopWeb) {
    return (
      <TouchableOpacity style={[styles.desktopCard, themedStyles.card]} onPress={onPress} activeOpacity={0.9}>
        {showImage ? <Image source={typeof imageSource === 'number' ? imageSource : { uri: imageSource || '' }} style={styles.desktopImage} resizeMode="cover" resizeMethod="resize" onError={() => { if (!usedImageFallback) { setUsedImageFallback(true); setImageSource(getWebFallbackImage(activity)); } else setImageFailed(true); }} /> : <LinearGradient colors={theme.eventCard.fallbackGradient} style={styles.desktopImage} />}
        <View style={styles.desktopInfo}>
          <Text style={[styles.desktopTitle, { color: theme.colors.textPrimary }]} numberOfLines={1}>{translatedActivity.translatingTitle ? '...' : translatedActivity.title}</Text>
          <Text style={[styles.desktopMeta, { color: theme.colors.textSecondary }]} numberOfLines={1}>{dateLabel}{activity.endTime ? ` – ${activity.endTime}` : ''}</Text>
          <Text style={[styles.desktopMeta, { color: theme.colors.textSecondary }]} numberOfLines={1}>{location}</Text>
          <View style={styles.desktopBottom}>
            <View style={[styles.desktopCategory, { backgroundColor: theme.colors.accentSecondarySoft, borderColor: theme.colors.accentSecondaryBorder }]}><Text style={[styles.desktopCategoryText, { color: theme.colors.accentSecondary }]}>{activity.category || visibleTags[0] || t('event.event')}</Text></View>
            <View style={styles.desktopActions}>
              <TouchableOpacity onPress={(event) => { event.stopPropagation(); onInterestPress?.(); }} style={styles.desktopIconAction}><Icon name={interested ? 'bookmark' : 'bookmark'} size={16} color={interested ? '#F58538' : theme.colors.iconSubtle} /></TouchableOpacity>
              <TouchableOpacity onPress={(event) => { event.stopPropagation(); onSharePress?.(); }} style={styles.desktopIconAction}><Icon name="share-2" size={15} color={theme.colors.iconSubtle} /></TouchableOpacity>
            </View>
          </View>
        </View>
      </TouchableOpacity>
    );
  }

  return (
    <TouchableOpacity style={[styles.card, themedStyles.card]} onPress={onPress} activeOpacity={0.92}>
      {showImage ? (
        <Image
          source={typeof imageSource === 'number' ? imageSource : { uri: imageSource || '' }}
          style={styles.image}
          resizeMode="cover" resizeMethod="resize"
          onLoad={() => {
            if (__DEV__) {
              console.log('IMAGE LOADED', activity.title, imageSource);
              console.log('[Tonyte][RN][EventImage] image load success', {
                eventId: activity.id,
                eventTitle: activity.title,
                selectedImageField: imageDebug?.selectedField,
                rawImageValue: imageDebug?.rawValue,
                resolvedImageValue: imageDebug?.resolvedValue,
                sourceType: imageDebug?.sourceType,
              });
            }
          }}
          onError={(event) => {
            // The resolved URL can 404 or — confirmed live for tonyte.app's
            // hosted /event-images/ paths — return HTTP 200 with an HTML
            // fallback page instead of the actual file (a missing-upload
            // issue on that server, not something this app controls).
            // Either way <Image> correctly refuses to render it. Swap to
            // the same themed Unsplash fallback resolveActivityImage()
            // already uses for the "no image field at all" case, once,
            // before giving up and falling through to the gradient below.
            if (!usedImageFallback) {
              setUsedImageFallback(true);
              setImageSource(getWebFallbackImage(activity));
            } else {
              setImageFailed(true);
            }
            if (__DEV__) {
              console.log('IMAGE ERROR', activity.title, imageSource, event.nativeEvent);
              console.warn('[Tonyte][RN][EventImage] image load error', {
                eventId: activity.id,
                eventTitle: activity.title,
                selectedImageField: imageDebug?.selectedField,
                rawImageValue: imageDebug?.rawValue,
                resolvedImageValue: imageDebug?.resolvedValue,
                sourceType: imageDebug?.sourceType,
                nativeEvent: event.nativeEvent,
              });
            }
          }}
        />
      ) : (
        <LinearGradient
          colors={theme.eventCard.fallbackGradient}
          locations={[0, 0.48, 1]}
          style={styles.image}
        >
          <AmbientGlow tone="teal" size={260} intensity={0.72} style={styles.fallbackGlowTeal} />
          <AmbientGlow tone="orange" size={300} intensity={0.62} style={styles.fallbackGlowOrange} />
          <Icon name="calendar" size={30} color="rgba(255,255,255,0.42)" />
        </LinearGradient>
      )}

      <LinearGradient
        colors={theme.eventCard.imageOverlay}
        locations={[0, 0.38, 0.68, 1]}
        style={styles.imageOverlay}
      />
      <LinearGradient
        colors={theme.eventCard.colorWash}
        start={{ x: 0.12, y: 0 }}
        end={{ x: 1, y: 0.7 }}
        style={[styles.colorWash, themedStyles.colorWash]}
      />
      <View pointerEvents="none" style={[styles.cardEdgeHighlight, themedStyles.cardEdgeHighlight]} />

      <View style={[styles.topRow, themedStyles.topRow]}>
        <LinearGradient colors={['rgba(38,208,206,0.45)', 'rgba(242,125,38,0.38)']} style={styles.avatarMarker}>
          {uploaderImage && !uploaderImageFailed ? (
            <Image
              source={{ uri: uploaderImage }}
              style={styles.avatarImage}
              resizeMode="cover" resizeMethod="resize"
              onError={() => setUploaderImageFailed(true)}
            />
          ) : (
            <Image source={TONYTE_PROFILE_IMAGE} style={[styles.avatarImage, styles.avatarFallbackImage]} resizeMode="contain" />
          )}
        </LinearGradient>
        <View style={styles.topCopy}>
          <Text style={[styles.headerTitle, themedStyles.headerTitle]} numberOfLines={2}>
            {translatedActivity.translatingTitle ? '...' : translatedActivity.title}
          </Text>
          <View style={styles.metaLine}>
            <Icon name="calendar" size={11} color={Colors.turquoise} />
            <Text style={[styles.metaLineText, themedStyles.headerMeta]} numberOfLines={1}>
              {dateLabel}
            </Text>
          </View>
        </View>
        <View style={[styles.categoryBadge, themedStyles.categoryBadge]}>
          <Text style={[styles.categoryBadgeText, themedStyles.categoryBadgeText]}>{activity.category}</Text>
        </View>
      </View>
      <View style={styles.centerCopy}>
        <LinearGradient
          pointerEvents="none"
          colors={theme.eventCard.infoGradient}
          locations={[0, 0.58, 1]}
          start={{ x: 0.5, y: 0 }}
          end={{ x: 0.5, y: 1 }}
          style={[styles.centerTextOverlay, themedStyles.centerTextOverlay]}
        />
        <View style={styles.locationRow}>
          <Icon name="map-pin" size={12} color={Colors.sunset} />
          <Text style={[styles.venueText, themedStyles.infoPrimaryText]} numberOfLines={1}>{location}</Text>
        </View>
        <View style={styles.entryRow}>
          <Text style={[styles.entryText, themedStyles.entryText]} numberOfLines={1}>{priceLabel}</Text>
          {statusText ? (
            <Text style={[styles.entryMuted, themedStyles.infoSecondaryText]} numberOfLines={1}>{statusText}</Text>
          ) : null}
        </View>
        <View style={styles.tagRow}>
          {visibleTags.map((tag, index) => (
            <View key={`${activity.id}-${String(tag)}-${index}`} style={[styles.tagPill, themedStyles.tagPill]}>
              <Text style={[styles.tagText, themedStyles.tagText]} numberOfLines={1}>{String(tag).replace(/^#/, '')}</Text>
            </View>
          ))}
          {hiddenTagCount > 0 ? (
            <View style={[styles.tagPill, styles.moreTagPill, themedStyles.tagPill]}>
              <Text style={[styles.tagText, themedStyles.tagText]}>+{hiddenTagCount}</Text>
            </View>
          ) : null}
        </View>
      </View>

      <View style={styles.bottomRow}>
        <View style={styles.bottomCopy}>
          <Text style={styles.timeText}>{ongoing ? t('feed.nowOn') : (activity.startTime || '')}</Text>
          {statusText ? (
            <Text style={[styles.statusText, themedStyles.bottomStatusText]} numberOfLines={1}>{statusText}</Text>
          ) : null}
          <Text style={[styles.goingText, themedStyles.bottomMutedText]}>{tp('feed.peopleGoingCount', count)}</Text>
        </View>
        <View style={styles.actionRow}>
          {canManageFeatured ? (
            <TouchableOpacity
              style={[styles.roundAction, themedStyles.mediaAction, featuredNow && styles.featuredActionActive]}
              activeOpacity={0.82}
              onPress={(event: GestureResponderEvent) => {
                event.stopPropagation();
                onFeaturedPress?.();
              }}
            >
              <Icon name="star" size={15} color={featuredNow ? Colors.sunset : theme.colors.mediaActionIcon} />
            </TouchableOpacity>
          ) : null}
          {canManage ? (
            <>
              <TouchableOpacity
                activeOpacity={0.82}
                style={[styles.manageButton, themedStyles.manageButton]}
                onPress={(event: GestureResponderEvent) => {
                  event.stopPropagation();
                  onEditPress?.();
                }}
              >
                <Text style={[styles.manageButtonText, themedStyles.manageButtonText]}>{t('common.edit')}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                activeOpacity={0.82}
                style={[styles.manageButton, themedStyles.deleteButton]}
                onPress={(event: GestureResponderEvent) => {
                  event.stopPropagation();
                  onDeletePress?.();
                }}
              >
                <Text style={[styles.manageButtonText, styles.manageDeleteText, themedStyles.deleteButtonText]}>{t('common.delete')}</Text>
              </TouchableOpacity>
            </>
          ) : null}
          <TouchableOpacity
            onPress={(event: GestureResponderEvent) => {
              event.stopPropagation();
              onInterestPress?.();
            }}
            style={[styles.roundAction, themedStyles.mediaAction, interested && styles.roundActionActive]}
            activeOpacity={0.82}
          >
            <Icon name={interested ? 'heart' : 'heart'} size={16} color={interested ? '#fff' : theme.colors.mediaActionIcon} />
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.roundAction, themedStyles.mediaAction]}
            activeOpacity={0.82}
            onPress={(event: GestureResponderEvent) => {
              event.stopPropagation();
              onSharePress?.();
            }}
          >
            <Icon name="share-2" size={15} color={theme.colors.mediaActionIcon} />
          </TouchableOpacity>
        </View>
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  desktopCard: { flex: 1, width: '100%', height: 226, borderRadius: 12, borderWidth: 1, overflow: 'hidden', marginBottom: 0 },
  desktopImage: { width: '100%', height: 124 },
  desktopInfo: { flex: 1, paddingHorizontal: 13, paddingTop: 9, paddingBottom: 7 },
  desktopTitle: { fontSize: 14, lineHeight: 18, fontWeight: '700', marginBottom: 2 },
  desktopMeta: { fontSize: 11, lineHeight: 15 },
  desktopBottom: { marginTop: 'auto', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  desktopCategory: { borderWidth: 1, borderRadius: 6, paddingHorizontal: 7, paddingVertical: 2 },
  desktopCategoryText: { fontSize: 9, fontWeight: '700', textTransform: 'capitalize' },
  desktopActions: { flexDirection: 'row', alignItems: 'center', gap: 3 },
  desktopIconAction: { width: 27, height: 27, alignItems: 'center', justifyContent: 'center' },
  card: {
    width: CARD_WIDTH,
    height: CARD_HEIGHT,
    alignSelf: 'center',
    marginBottom: 13,
    overflow: 'hidden',
    borderRadius: 19,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.13)',
    backgroundColor: 'rgba(7,16,29,0.86)',
    ...Shadow.card,
  },
  image: {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  imageOverlay: {
    ...StyleSheet.absoluteFill,
  },
  colorWash: {
    ...StyleSheet.absoluteFill,
    opacity: 0.7,
  },
  cardEdgeHighlight: {
    position: 'absolute',
    top: 1,
    left: 18,
    right: 18,
    height: 1,
    borderRadius: 1,
  },
  fallbackGlowTeal: {
    left: -135,
    top: -120,
  },
  fallbackGlowOrange: {
    right: -160,
    bottom: -150,
  },
  topRow: {
    position: 'absolute',
    flexDirection: 'row',
    alignItems: 'center',
    left: 0,
    right: 0,
    top: 0,
    gap: 9,
    minHeight: 66,
    paddingHorizontal: 13,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.10)',
    backgroundColor: 'rgba(3,8,16,0.72)',
  },
  avatarMarker: {
    width: 45,
    height: 45,
    borderRadius: 23,
    padding: 1.4,
    backgroundColor: '#09101A',
    shadowColor: Colors.turquoise,
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.26,
    shadowRadius: 10,
    elevation: 5,
  },
  avatarImage: {
    width: '100%',
    height: '100%',
    borderRadius: 22,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.20)',
    backgroundColor: 'rgba(9,15,26,0.76)',
  },
  avatarFallbackImage: {
    borderRadius: 22,
    backgroundColor: 'rgba(9,15,26,0.88)',
  },
  topCopy: {
    flex: 1,
    minWidth: 0,
  },
  headerTitle: {
    color: '#fff',
    fontSize: 15,
    lineHeight: 18,
    fontWeight: Typography.extrabold,
    textShadowColor: 'rgba(0,0,0,0.45)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 3,
  },
  venueText: {
    color: 'rgba(255,255,255,0.93)',
    flex: 1,
    fontSize: 12,
    lineHeight: 15,
    fontWeight: Typography.bold,
  },
  metaLine: {
    marginTop: 4,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
  },
  metaLineText: {
    color: 'rgba(255,255,255,0.84)',
    fontSize: 11,
    lineHeight: 14,
    fontWeight: Typography.bold,
  },
  categoryBadge: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.14)',
    backgroundColor: 'rgba(255,255,255,0.12)',
  },
  categoryBadgeText: {
    color: 'rgba(255,255,255,0.78)',
    fontSize: 9,
    lineHeight: 11,
    fontWeight: Typography.extrabold,
    textTransform: 'uppercase',
  },
  manageActions: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 6,
  },
  manageButton: {
    paddingHorizontal: 9,
    paddingVertical: 6,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.18)',
    backgroundColor: 'rgba(10,14,22,0.55)',
  },
  manageButtonText: {
    color: '#fff',
    fontSize: 10,
    lineHeight: 12,
    fontWeight: Typography.extrabold,
  },
  manageDeleteText: {
    color: Colors.sunset,
  },
  centerCopy: {
    position: 'absolute',
    left: 64,
    right: 20,
    top: 84,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 18,
    overflow: 'hidden',
  },
  centerTextOverlay: {
    ...StyleSheet.absoluteFill,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.06)',
  },
  locationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  entryRow: {
    marginTop: 7,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  entryText: {
    color: 'rgba(255,185,144,0.94)',
    fontSize: 11,
    lineHeight: 13,
    fontWeight: Typography.extrabold,
    textTransform: 'uppercase',
  },
  entryMuted: {
    flex: 1,
    color: 'rgba(255,255,255,0.6)',
    fontSize: 11,
    lineHeight: 13,
    fontWeight: Typography.medium,
  },
  tagRow: {
    marginTop: 9,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'stretch',
    gap: 6,
  },
  tagPill: {
    maxWidth: 76,
    borderRadius: Radius.pill,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
    backgroundColor: 'rgba(5,8,14,0.48)',
    paddingHorizontal: 9,
    paddingVertical: 5,
  },
  moreTagPill: {
    minWidth: 38,
    alignItems: 'center',
  },
  tagText: {
    color: 'rgba(226,232,240,0.86)',
    fontSize: 10,
    lineHeight: 12,
    fontWeight: Typography.bold,
    textAlign: 'center',
  },
  bottomRow: {
    position: 'absolute',
    left: 16,
    right: 16,
    bottom: 14,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  bottomCopy: {
    flex: 1,
    minWidth: 0,
  },
  timeText: {
    color: Colors.turquoise,
    fontSize: 13,
    lineHeight: 16,
    fontWeight: Typography.extrabold,
  },
  statusText: {
    color: 'rgba(255,255,255,0.74)',
    fontSize: 11,
    lineHeight: 14,
    marginTop: 4,
    maxWidth: CARD_WIDTH - 125,
    fontWeight: Typography.medium,
  },
  goingText: {
    color: 'rgba(255,255,255,0.58)',
    fontSize: 10,
    lineHeight: 13,
    marginTop: 3,
    fontWeight: Typography.medium,
  },
  actionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
  },
  roundAction: {
    width: 34,
    height: 34,
    borderRadius: 17,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.2)',
    backgroundColor: 'rgba(5,8,14,0.46)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  roundActionActive: {
    borderColor: 'rgba(255,255,255,0.34)',
    backgroundColor: 'rgba(242,125,38,0.75)',
  },
  featuredActionActive: {
    borderColor: 'rgba(242,125,38,0.48)',
    backgroundColor: 'rgba(242,125,38,0.16)',
  },
});

const createThemeStyles = (theme: TonyteTheme) => StyleSheet.create({
  card: {
    backgroundColor: theme.eventCard.shellBackground,
    borderColor: theme.eventCard.shellBorder,
    ...theme.shadows.card,
  },
  colorWash: {
    opacity: theme.eventCard.colorWashOpacity,
  },
  cardEdgeHighlight: {
    backgroundColor: theme.eventCard.edgeHighlight,
  },
  topRow: {
    backgroundColor: theme.eventCard.headerBackground,
    borderBottomColor: theme.eventCard.headerBorder,
  },
  headerTitle: {
    color: theme.eventCard.headerTitle,
    textShadowColor: theme.eventCard.headerTextShadow,
  },
  headerMeta: {
    color: theme.eventCard.headerMeta,
  },
  categoryBadge: {
    backgroundColor: theme.eventCard.categoryBackground,
    borderColor: theme.eventCard.categoryBorder,
  },
  categoryBadgeText: {
    color: theme.eventCard.categoryText,
  },
  centerTextOverlay: {
    borderColor: theme.eventCard.infoBorder,
  },
  infoPrimaryText: {
    color: theme.eventCard.infoPrimaryText,
  },
  infoSecondaryText: {
    color: theme.eventCard.infoSecondaryText,
  },
  entryText: {
    color: theme.eventCard.entryText,
  },
  tagPill: {
    backgroundColor: theme.eventCard.chipBackground,
    borderColor: theme.eventCard.chipBorder,
  },
  tagText: {
    color: theme.eventCard.chipText,
  },
  bottomStatusText: {
    color: theme.eventCard.bottomStatusText,
  },
  bottomMutedText: {
    color: theme.eventCard.bottomMutedText,
  },
  manageButton: {
    backgroundColor: theme.eventCard.manageBackground,
    borderColor: theme.eventCard.manageBorder,
  },
  manageButtonText: {
    color: theme.eventCard.manageText,
  },
  deleteButton: {
    backgroundColor: theme.eventCard.deleteBackground,
    borderColor: theme.eventCard.deleteBorder,
  },
  deleteButtonText: {
    color: theme.eventCard.deleteText,
  },
  mediaAction: {
    backgroundColor: theme.colors.mediaActionBackground,
    borderColor: theme.colors.mediaActionBorder,
  },
});

export default memo(EventCard);
